import { z } from 'zod';
import { PushInteractionOperation } from '../push/sdk-interaction.ts';
import { SdkDebugDirective } from '../sdk/debug-logs.ts';
import { InstallAttributionOperation } from './attribution.ts';
import { CardInteractionOperation } from './card-interaction.ts';
import { AttributeChange, EventName, IsoDateTime, OperationEnvelope, OperationResult, Properties } from './common.ts';
import { SdkIdentity } from './identity.ts';
import { ChannelStates, GroupState } from './subscriptions.ts';

/** `POST /sdk/v1/batch` (BB-25). Operations are parsed one by one so a bad one fails alone. */
export const SdkBatchRequest = z.object({
  schemaVersion: z.literal(1),
  batchId: z.uuid(),
  sentAt: IsoDateTime,
  installationId: z.uuid(),
  identity: SdkIdentity,
  sdk: z.object({ platform: z.enum(['android', 'ios', 'web']), version: z.string().min(1).max(32) }),
  app: z.object({ version: z.string().min(1).max(64), build: z.string().max(64).optional() }),
  device: z
    .object({
      model: z.string().max(128).optional(),
      osVersion: z.string().max(64).optional(),
      locale: z.string().max(35).optional(),
      timezone: z.string().max(64).optional(),
    })
    .optional(),
  operations: z.array(OperationEnvelope).min(1).max(100),
});
export type SdkBatchRequest = z.infer<typeof SdkBatchRequest>;

const op = { operationId: z.uuid(), occurredAt: IsoDateTime };

export const SdkOperation = z.discriminatedUnion('type', [
  z.object({ type: z.literal('event'), ...op, sessionId: z.uuid().optional(), name: EventName, properties: Properties.optional() }),
  z.object({ type: z.literal('attributes'), ...op, changes: z.array(AttributeChange).min(1).max(50) }),
  z.object({ type: z.literal('session_start'), ...op, sessionId: z.uuid() }),
  z.object({ type: z.literal('session_end'), ...op, sessionId: z.uuid(), durationMs: z.number().int().min(0) }),
  z.object({
    type: z.literal('push_token'),
    ...op,
    provider: z.enum(['fcm', 'apns']),
    token: z.string().max(4096).nullable(),
    apnsEnvironment: z.enum(['sandbox', 'production']).optional(),
  }),
  z.object({
    type: z.literal('push_permission'),
    ...op,
    status: z.enum(['granted', 'denied', 'provisional', 'not_determined']),
    /** Android: the notification channels the app created and whether the user left them on (channel coverage, BB-77). */
    channels: z.array(z.object({ id: z.string().min(1).max(255), importance: z.number().int().min(0).max(5), enabled: z.boolean() })).max(100).optional(),
  }),
  z.object({ type: z.literal('subscription'), ...op, channels: ChannelStates.optional(), groups: z.array(GroupState).max(50).optional() }),
  InstallAttributionOperation,
  CardInteractionOperation,
  PushInteractionOperation,
]);
export type SdkOperation = z.infer<typeof SdkOperation>;

/** Unknown response fields are ignored by SDKs (baseline §9). */
export const BatchResponse = z.looseObject({
  schemaVersion: z.literal(1),
  batchId: z.uuid(),
  receivedAt: IsoDateTime,
  results: z.array(OperationResult),
  /** Present while a debug session collects this installation's SDK log (BB-157). */
  debug: SdkDebugDirective.optional(),
});
export type BatchResponse = z.infer<typeof BatchResponse>;
